import type {
  RoutineDifficultyOptionsDTO,
  RoutineDifficultyOptions,
  TreatmentOverviewDTO,
} from "@akina/domain";
import { toCamelCase } from "@akina/utils";
import type {
  ReviewTask,
  ReviewTaskApi,
  FeedbacksSummaryDTO,
  FeedbacksSummaryDTOApi,
  TreatmentSummaryResponse,
  TreatmentSummaryResponseApi,
  TherapistFeedback,
  TherapistFeedbackTypeEnum,
  PresignedBucketUrlApi,
} from "types/schemas";
import { ApiMethod, type ApiConfiguration } from "..";
import { routineDifficultyOptionsMapper } from "../mappers";
import { uploadFileToS3 } from "./shared";

export const sessionReviewApi = (config: ApiConfiguration) => ({
  async uploadFeedback(params: {
    sessionId: string;
    mediaType: TherapistFeedbackTypeEnum | null | undefined;
    routineId?: number;
    file: File;
  }): Promise<string> {
    const response = await config.httpService.fetch<PresignedBucketUrlApi>({
      method: ApiMethod.GET,
      url: config.endpoints.sessionReview.mediaStorage(params.sessionId, params.mediaType, params.routineId),
    });

    await uploadFileToS3(response.presigned_url, params.file);

    return response.object_name;
  },
  async addSessionFeedback(params: { sessionId: string; feedback: TherapistFeedback; file?: File }): Promise<string> {
    if (params.file && params.feedback.mediaType) {
      const mediaObjectName = await this.uploadFeedback({
        sessionId: params.sessionId,
        mediaType: params.feedback.mediaType,
        file: params.file,
      });

      await config.httpService.fetch({
        method: ApiMethod.PATCH,
        url: config.endpoints.sessionReview.sessionFeedback,
        data: {
          session_log_id: params.sessionId,
          general_feedback: {
            text: params.feedback.text,
            media_type: params.feedback.mediaType,
            media_object_name: mediaObjectName,
          },
        },
      });
    } else {
      await config.httpService.fetch({
        method: ApiMethod.PATCH,
        url: config.endpoints.sessionReview.sessionFeedback,
        data: {
          session_log_id: params.sessionId,
          general_feedback: {
            text: params.feedback.text,
            media_type: params.feedback.mediaType,
          },
        },
      });
    }

    return params.sessionId;
  },
  async deleteSessionFeedback(sessionId: string): Promise<string> {
    await config.httpService.fetch<{
      treatments: TreatmentOverviewDTO[];
    }>({
      method: ApiMethod.DELETE,
      url: config.endpoints.sessionReview.sessionFeedback,
      data: {
        session_log_id: sessionId,
      },
    });

    return sessionId;
  },
  async addRoutineFeedback(params: {
    sessionId: string;
    routineId: number;
    feedback: TherapistFeedback;
    file?: File;
  }): Promise<string> {
    if (params.file && params.feedback.mediaType) {
      const mediaObjectName = await this.uploadFeedback({
        sessionId: params.sessionId,
        routineId: params.routineId,
        mediaType: params.feedback.mediaType,
        file: params.file,
      });

      await config.httpService.fetch<{
        treatments: TreatmentOverviewDTO[];
      }>({
        method: ApiMethod.PATCH,
        url: config.endpoints.sessionReview.routineFeedback.base,
        data: {
          session_log_id: params.sessionId,
          routine_log_id: params.routineId,
          routine_feedback: {
            text: params.feedback.text,
            media_type: params.feedback.mediaType,
            media_object_name: mediaObjectName,
          },
        },
      });
    } else {
      await config.httpService.fetch<{
        treatments: TreatmentOverviewDTO[];
      }>({
        method: ApiMethod.PATCH,
        url: config.endpoints.sessionReview.routineFeedback.base,
        data: {
          session_log_id: params.sessionId,
          routine_log_id: params.routineId,
          routine_feedback: {
            text: params.feedback.text,
            media_type: params.feedback.mediaType,
          },
        },
      });
    }

    return params.sessionId;
  },
  async deleteRoutineFeedback(params: { sessionId: string; routineId: number }): Promise<string> {
    await config.httpService.fetch<{
      treatments: TreatmentOverviewDTO[];
    }>({
      method: ApiMethod.DELETE,
      url: config.endpoints.sessionReview.routineFeedback.base,
      data: {
        session_log_id: params.sessionId,
        routine_log_id: params.routineId,
      },
    });

    return params.sessionId;
  },

  async getTreatmentSummary(treatmentId: string): Promise<TreatmentSummaryResponse> {
    const result = await config.httpService.fetch<TreatmentSummaryResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.sessionReview.treatmentSummary(treatmentId),
    });

    return toCamelCase(result);
  },
  async getReviewTask(sessionId: string): Promise<ReviewTask> {
    const result = await config.httpService.fetch<ReviewTaskApi>({
      method: ApiMethod.GET,
      url: config.endpoints.sessionReview.reviewTask(sessionId),
    });

    return toCamelCase<ReviewTaskApi, ReviewTask>(result);
  },

  async getRoutineDifficulties(sessionId: string, routineId: number): Promise<RoutineDifficultyOptions> {
    const result = await config.httpService.fetch<RoutineDifficultyOptionsDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.sessionReview.routineDifficulties(sessionId, routineId),
    });

    return routineDifficultyOptionsMapper.toDomain(result);
  },
  async setReviewed(sessionId: string, forwardToSupport: boolean): Promise<string> {
    await config.httpService.fetch<void>({
      method: ApiMethod.PATCH,
      url: config.endpoints.sessionReview.reviewed,
      data: {
        session_log_id: sessionId,
        forward_to_support: forwardToSupport,
      },
    });

    return sessionId;
  },

  async getFeedbacksSummary(therapistId: string, treatmentId?: string): Promise<FeedbacksSummaryDTO> {
    const result = await config.httpService.fetch<FeedbacksSummaryDTOApi>({
      method: ApiMethod.GET,
      url: config.endpoints.sessionReview.feedbacksSummary(therapistId, treatmentId),
    });

    return toCamelCase(result);
  },
});
